'use client';

/**
 * Portal Layout Client Component
 * 
 * Wrapper that conditionally shows either the portal layout (for authenticated users)
 * or the auth layout (for login/register screens).
 * 
 * Uses fixed positioning to break out of any parent container constraints
 * (e.g., AuthLayoutClient's centered card).
 */

import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { AuthScreen } from '@/components/auth/AuthScreen';
import { PortalLayout } from './PortalLayout';
import { Loader2 } from 'lucide-react';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';

interface PortalLayoutClientProps {
  children: ReactNode;
}

export function PortalLayoutClient({ children }: PortalLayoutClientProps) {
  const { customer, isLoading, hasSupabaseSession, error } = useCustomerAuth();
  const pathname = usePathname();
  
  // Extract locale from current pathname (e.g., /en/account -> en)
  const localeMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = localeMatch ? localeMatch[1] : 'en';

  // Loading state - use fixed positioning to cover full viewport
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-stone-50 flex flex-col items-center justify-center z-50">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        <p className="mt-4 text-sm text-stone-400">Loading your account...</p>
      </div>
    );
  }

  // User is authenticated with Supabase but customer profile sync failed
  if (hasSupabaseSession && !customer && error) {
    return (
      <div className="fixed inset-0 bg-stone-50 flex items-center justify-center p-4 z-50">
        <div className="max-w-md w-full bg-white rounded-3xl border border-stone-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-stone-900 mb-2">Account Setup Issue</h1>
          <p className="text-stone-500 text-sm mb-4">
            We couldn&apos;t complete your account setup. This might be a temporary issue.
          </p>
          {error.message && (
            <p className="text-red-600 text-xs mb-4 p-2 bg-red-50 rounded-xl border border-red-100">
              {error.message}
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 rounded-xl bg-stone-900 text-white font-medium hover:bg-stone-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated - show auth screen (AuthScreen handles its own layout)
  if (!customer) {
    return <AuthScreen redirectTo={`/${locale}/account`} />;
  }

  // Authenticated - show portal layout
  return <PortalLayout>{children}</PortalLayout>;
}
